import axios from 'axios';
import stringHash from 'string-hash';

import { WaxAuthClient } from 'wax-auth-client';
import { storeAppDispatch } from '../../../GlobalState/Store';
import { setPlayerLogout } from '../../../reducers/UserReducer';
import { client } from '../../../helpers/client';
import { UserService } from '../../../services/UserService';
import { setBridgeToken } from '../play/unityBridge';
import { isLocal, isTestnet } from '../../../constants/envs';
import { nukeAnchorSavedAccount } from '../../../services/autoLogin';
import get from 'lodash/get';
import Swal from 'sweetalert2';
import { authFlowDebug } from '../../../debug/authFlowDebug';

const authClient = new WaxAuthClient();
sessionStorage.removeItem('verified');


// --------- utils

const failureHandler = ({ reason, err }) => {
  authFlowDebug('verify.failure', {
    reason,
    errMessage: err && err.message,
    errStatus: get(err, 'response.status'),
    errData: get(err, 'response.data'),
  });
  Swal.fire({text: `Verification error. Please try again`})
  console.error(`Verification failure details:\n-Reason: ${reason}\n-Error: ${err}`);
  storeAppDispatch(setPlayerLogout());
  window.location.reload();
  throw new Error('verify-ko');
};


// ADD USER-AGENT HASH anti-cheat. Multi tab/window check.
// - send it and store it in login request
// - send it in all claim/stake/unstake requests
// - check it srv side and 400 if mismatch
const addDefaultHeaders = () => {
  // FE version
  const version = get(window, 'version.wrapper');
  axios.defaults.headers.common['X-FE-VERSION'] = `R_v${version}`;
  // User Agent
  const uaHash = stringHash(navigator.userAgent);
  axios.defaults.headers.common['X-Client-ID'] = uaHash;
};


// --------- WAX

export const verifyWaxUser = async ({ username }) => {
  console.debug('[vwu] Starting verification for', username);
  authFlowDebug('verify.wax.start', { username });
  // ## NONCE
  let nonceResponse;
  try {
    isTestnet && console.debug('[vwu] getting nonce for', username);
    nonceResponse = await client.post(`/api/auth/getNonce`, { waxAddress: username });
    authFlowDebug('verify.wax.nonce.ok', {
      username,
      hasNonce: !!get(nonceResponse, 'data.nonce'),
    });
  } catch (err) {
    failureHandler({ reason: 'caught-getnonce', err});
    return ({ err: 'error-getting-nonce' });
  }
  const nonce = nonceResponse.data.nonce;
  isLocal && console.debug('[vwu] getting tx data:', { nonce, username });
  const tx = authClient.getTransactionData(nonce, username);
  let result;
  // ## SIGNATURE 1
  try {
    authFlowDebug('verify.wax.sign.start', { username });
    result = await UserService.session.signTransaction(tx.data, tx.options);
    authFlowDebug('verify.wax.sign.ok', {
      username,
      signaturesCount: get(result, 'transaction.signatures.length'),
    });
  } catch (err) {
    console.error('[vwu] Error signing:', { nonce, tx, err });
    nukeAnchorSavedAccount();
    return ({ err: 'error-signing-tx' });
  }
  // ## VERIFY (BACKEND)
  const proof = {
    serializedTransaction: result.transaction.serializedTransaction,
    signatures: result.transaction.signatures
  };
  const payload = { proof, waxAddress: username, nonce };
  let verificationResult;
  try {
    isTestnet && console.debug('<><> verifying sig');
    addDefaultHeaders(); // applied to all API calls from now on
    authFlowDebug('verify.wax.backend.start', { username });
    verificationResult = await client.post(`/api/auth/verify`, payload);
    authFlowDebug('verify.wax.backend.ok', {
      username,
      status: verificationResult.status,
      result: get(verificationResult, 'data.result'),
      hasToken: !!get(verificationResult, 'data.token'),
    });
  } catch (err) {
    failureHandler({ reason: 'caught-verify', err});
    return ({ err: 'error-verify-call' });
  }
  isLocal && console.debug('[vu] Verification result', verificationResult);
  if (verificationResult.data.result !== 'success') {
    failureHandler({ reason: '401-result', err: 'verification response is not success' });
    return ({ err: 'verify-call-failed' });
  }
  // ## LOGIN SUCCESS ACTIONS
  const headerValue = `Bearer ${verificationResult.data.token}`;
  axios.defaults.headers.common['Authorization'] = headerValue;
  setBridgeToken({ headerValue, username });
  sessionStorage.setItem('verified', Date.now());
  isLocal && console.log('Wax user correctly authenticated!'); // toast.success
  authFlowDebug('verify.wax.done', { username });
  return ({});
};


// ----------- Used for HIVE, SKL, ETH. For Wax double signature crap.

export const verifyUserIdentity = async ({
  username, chainLogin,
  idToken, // Immutable Passport Flow
  message, signed_message, // all other chains
}) => {
  authFlowDebug('verify.identity.start', {
    username,
    chainLogin,
    hasIdToken: !!idToken,
    hasSignedMessage: !!signed_message,
  });
  // ## VERIFY (message already signed)
  addDefaultHeaders(); // applied to all API calls from now on
  const resUserSigVerify = await client.post('/api/auth/verify', // Note: chainLogin is already appended as param in the client
    {
      username,
      message, signed_message, // all other chains
      idToken, // imx
    },
  );
  if (resUserSigVerify.status !== 200 || resUserSigVerify.data.result !== 'success') {
    failureHandler({ reason: 'verify-ko', err: resUserSigVerify });
  }
  authFlowDebug('verify.identity.backend.ok', {
    username,
    chainLogin,
    status: resUserSigVerify.status,
    result: get(resUserSigVerify, 'data.result'),
    hasToken: !!get(resUserSigVerify, 'data.token'),
  });
  // ## LOGIN SUCCESS ACTIONS
  // - Axios Auth header
  const headerValue = `Bearer ${resUserSigVerify.data.token}`;
  axios.defaults.headers.common['Authorization'] = headerValue;
  // - Unity
  setBridgeToken({ headerValue, username });
  // - Browser Storage
  sessionStorage.setItem('verified', Date.now());
  localStorage.setItem(`${chainLogin}Username`, username);

  isLocal && console.log(`${chainLogin} user correctly authenticated!`); // toast.success
  authFlowDebug('verify.identity.done', { username, chainLogin });
  return true;
};
